<script setup lang="ts">
/*
 * Full-screen console search. A flat ink sheet with a huge input; results
 * are landscape tiles the focus cursor walks with the arrow keys (↓ from the
 * input enters the grid; typing from a tile returns to the input). Before
 * typing, SearchIdle shows recents, recently viewed and "Go to". Owns the
 * global shortcut too: ⌘K / Ctrl+K toggles, "/" opens outside text fields.
 */
import { Search, X } from 'lucide-vue-next';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useFocusCursor } from '../../composables/useFocusCursor';
import { useModalFocus } from '../../composables/useModalFocus';
import { SEARCH_MIN_CHARS, rememberSearch, useProductSearch } from '../../composables/useProductSearch';
import { useUiStore } from '../../stores/ui';
import TileWithInfo from '../catalog/TileWithInfo.vue';
import SearchIdle from './SearchIdle.vue';

/** One bento screen: 4 columns x 3 rows on desktop. */
const RESULT_LIMIT = 12;
const SKELETON_TILES = 8;
const STAGGER_MS = 30;

const { t } = useI18n();
const router = useRouter();
const ui = useUiStore();
const { query, loading, results, total, recent, reset } = useProductSearch(RESULT_LIMIT);

const dialog = ref<HTMLElement | null>(null);
const input = ref<HTMLInputElement | null>(null);
const open = computed(() => ui.paletteOpen);
useModalFocus(dialog, open, () => ui.closePalette(), { initial: input });

const term = computed(() => query.value.trim());
const searching = computed(() => term.value.length >= SEARCH_MIN_CHARS);
const showEmpty = computed(() => searching.value && !loading.value && results.value.length === 0);
const showSkeleton = computed(() => searching.value && loading.value && results.value.length === 0);

watch(open, (isOpen) => { if (isOpen) reset(); });

// The grid only exists while there are results, so the cursor binds to a
// wrapper that is always rendered.
const body = ref<HTMLElement | null>(null);
useFocusCursor(body);

function focusFirstTile() {
  body.value?.querySelector<HTMLElement>('[data-tile]')?.focus();
}

function onBodyKeydown(e: KeyboardEvent) {
  if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey && document.activeElement !== input.value) input.value?.focus();
}

function isTextField(el: EventTarget | null): boolean {
  return el instanceof HTMLElement && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));
}

function onGlobalKey(e: KeyboardEvent) {
  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
    e.preventDefault();
    if (ui.paletteOpen) ui.closePalette();
    else ui.openPalette();
  } else if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey && !ui.paletteOpen && !isTextField(e.target)) {
    e.preventDefault();
    ui.openPalette();
  }
}
onMounted(() => window.addEventListener('keydown', onGlobalKey));
onBeforeUnmount(() => window.removeEventListener('keydown', onGlobalKey));

function onSubmit() {
  if (!searching.value) return;
  rememberSearch(term.value);
  void router.push({ path: '/catalog', query: { q: term.value } });
  ui.closePalette();
}

// A link to the page you are already on never changes the route, so App's
// close-on-navigate would not fire; close on any link click instead.
function onBodyClick(e: MouseEvent) {
  if (!(e.target as HTMLElement).closest('a')) return;
  if (searching.value) rememberSearch(term.value);
  ui.closePalette();
}
</script>

<template>
  <div v-if="open" class="fixed inset-0 z-[75]">
    <div
      ref="dialog"
      role="dialog"
      aria-modal="true"
      :aria-label="t('header.palette.label')"
      tabindex="-1"
      class="animate-pop absolute inset-0 flex flex-col overflow-hidden bg-bg-base outline-none"
    >
      <div class="shrink-0 border-b border-border">
        <form class="mx-auto flex max-w-page items-center gap-4 px-4 py-4 md:px-8 md:py-6" role="search" @submit.prevent="onSubmit">
          <Search class="h-7 w-7 shrink-0 text-accent" aria-hidden="true" />
          <input
            ref="input"
            v-model="query"
            type="text"
            autocomplete="off"
            enterkeyhint="search"
            :aria-label="t('header.aria.search')"
            :placeholder="t('header.search.placeholder')"
            class="h-14 min-w-0 flex-1 border-0 border-b-2 border-border-strong bg-transparent font-heading text-[28px] font-bold text-text-base outline-none transition-colors placeholder:text-text-faint focus:border-accent"
            @keydown.down.prevent="focusFirstTile"
          />
          <kbd class="hidden shrink-0 rounded-md border border-border-strong px-2 py-1 font-heading text-[11px] font-bold text-text-dim sm:block">ESC</kbd>
          <button type="button" class="btn-icon shrink-0" :aria-label="t('common.actions.close')" @click="ui.closePalette()">
            <X class="h-5 w-5" aria-hidden="true" />
          </button>
        </form>
      </div>

      <div ref="body" class="flex-1 overflow-y-auto" @keydown="onBodyKeydown" @click="onBodyClick">
        <div class="mx-auto max-w-page px-4 py-8 md:px-8">
          <SearchIdle v-if="!searching" :recent="recent" />

          <p v-else-if="showEmpty" class="py-16 text-center font-heading text-[22px] font-bold text-text-dim">{{ t('common.state.noResults') }}</p>

          <div v-else class="flex flex-col gap-6" :aria-busy="loading">
            <div class="grid grid-cols-1 gap-4 p-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              <template v-if="showSkeleton">
                <div v-for="n in SKELETON_TILES" :key="n" class="skeleton aspect-video" />
              </template>
              <div v-for="(p, i) in results" v-else :key="p.id" class="animate-pop" :style="{ animationDelay: `${Math.min(i, 8) * STAGGER_MS}ms` }">
                <TileWithInfo :product="p" />
              </div>
            </div>
            <RouterLink
              v-if="total > results.length"
              :to="{ path: '/catalog', query: { q: term } }"
              class="btn-secondary self-center"
            >{{ t('header.search.seeAll', { count: total.toLocaleString() }, total) }}</RouterLink>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
